export interface RouteSelectionRow {
  routeId?: string;
  stops?: Array<{ isPhysicalDrRequired?: boolean }>;
  isPhysicalDrRequired?: boolean;
}

export interface RoutePrintContext {
  routeId: string;
  hasPhysicalDrNeed: boolean;
}

export interface PhysicalDrRouteContextOptions {
  isClosedTrailersGrid?: boolean; // Route selected in the driver stop list (Route, Manifest #, or Trailer # dropdown).
  selectedDriverRouteId?: string | null;
  driverRoutes?: RouteSelectionRow[]; // All routes for the selected driver (from DispatchStore.routesList).
}

// When the stop list shows a different route/trailer than the grid row's active route, use the stop-list selection for print context. Route, Manifest #, and Trailer # dropdowns all resolve to the same routeId via DriverPopUp.changeRouteData.
export function resolveEffectiveRouteRow(
  gridRow: RouteSelectionRow,
  options: Pick<
    PhysicalDrRouteContextOptions,
    "selectedDriverRouteId" | "driverRoutes"
  > = {}
): RouteSelectionRow {
  const { selectedDriverRouteId, driverRoutes } = options;
  if (!selectedDriverRouteId || !driverRoutes?.length) {
    return gridRow;
  }
  const stopListRoute = driverRoutes.find(
    (route) => route.routeId === selectedDriverRouteId
  );
  return stopListRoute ?? gridRow;
}

export function doesSelectionRowRequirePhysicalDr(
  row: RouteSelectionRow | undefined,
  isClosedTrailersGrid = false
): boolean {
  if (!row) {
    return false;
  }
  if (isClosedTrailersGrid) {
    return Boolean(row.isPhysicalDrRequired);
  }
  const stops = row.stops;
  if (Array.isArray(stops) && stops.length > 0) {
    return stops.some((stop) => Boolean(stop?.isPhysicalDrRequired));
  }
  return Boolean(row.isPhysicalDrRequired);
}

export function resolvePhysicalDrRouteContext(
  selectedRows?: RouteSelectionRow[] | null,
  options: PhysicalDrRouteContextOptions = {}
): RoutePrintContext | null {
  const { isClosedTrailersGrid = false, selectedDriverRouteId, driverRoutes } =
    options;
  if (!selectedRows?.length || selectedRows.length !== 1) {
    return null;
  }
  const gridRow = selectedRows[0];
  const selectedRow = isClosedTrailersGrid
    ? gridRow
    : resolveEffectiveRouteRow(gridRow, {
        selectedDriverRouteId,
        driverRoutes,
      });
  const routeId = selectedRow?.routeId;
  if (!routeId || String(routeId).trim() === "") {
    return null;
  }
  return {
    routeId: String(routeId).trim(),
    hasPhysicalDrNeed: doesSelectionRowRequirePhysicalDr(
      selectedRow,
      isClosedTrailersGrid
    ),
  };
}
